import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  computed,
  inject,
  input,
  LOCALE_ID,
  output,
  signal,
} from '@angular/core';
import { finalize, Subscription } from 'rxjs';
import { ConfirmationDialog } from '../../../../../shared/ui/confirmation-dialog/confirmation-dialog';
import { ErrorVTO, ProductConfigVTO } from '../../../../../pages/admin-dashboard/products/product.model';
import { ProductService } from '../../../../../services/product/product.service';
import { ToggleButton } from '../../../../../shared/ui/toggle-button/toggle-button';

@Component({
  selector: 'app-product-config-vat',
  standalone: true,
  imports: [ConfirmationDialog, ToggleButton],
  templateUrl: './product-config-vat.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductConfigVat implements OnDestroy {
  productId = input.required<number>();
  config = input.required<ProductConfigVTO>();
  changed = output<ProductConfigVTO>();

  private productService = inject(ProductService);
  private locale = inject(LOCALE_ID);
  private sub = new Subscription();

  isArabic = computed(() => this.locale.startsWith('ar'));

  pendingVat = signal<boolean | null>(null);
  isSaving = signal(false);
  errorSave = signal('');

  dialog = computed(() => {
    const enable = this.pendingVat();
    const ar = this.isArabic();
    const saving = this.isSaving();
    return {
      title: ar ? 'تأكيد' : 'Confirm',
      description: enable
        ? ar
          ? 'هل أنت متأكد من جعل هذا الصنف خاضعًا لضريبة القيمة المضافة؟'
          : 'Are you sure you want to mark this product as subject to VAT?'
        : ar
          ? 'هل أنت متأكد من إلغاء ضريبة القيمة المضافة لهذا الصنف؟'
          : 'Are you sure you want to remove VAT from this product?',
      confirmContent: ar ? (saving ? 'جارٍ الحفظ...' : 'تأكيد') : saving ? 'Saving...' : 'Confirm',
      cancelContent: ar ? 'إلغاء' : 'Cancel',
    };
  });

  onToggleClick() {
    if (this.isSaving()) return;

    this.errorSave.set('');
    this.pendingVat.set(!this.config().isVATApplied);
  }

  closeConfirmation() {
    if (this.isSaving()) return;
    this.pendingVat.set(null);
    this.errorSave.set('');
  }

  confirm() {
    const next = this.pendingVat();
    if (next === null) return;

    const c = this.config();
    const body: ProductConfigVTO = {
      isVATApplied: next,
      orderLimitQuantity: c.orderLimitQuantity ?? null,
      orderLimitDays: c.orderLimitDays ?? null,
      orderLimitTotalCash: c.orderLimitTotalCash ?? null,
    };

    this.isSaving.set(true);
    this.errorSave.set('');
    this.sub.add(
      this.productService
        .saveProductConfig(this.productId(), body)
        .pipe(finalize(() => this.isSaving.set(false)))
        .subscribe({
          next: () => {
            this.pendingVat.set(null);
            this.changed.emit(body);
          },
          error: (err: ErrorVTO) =>
            this.errorSave.set(
              this.isArabic() ? err?.messageAr || 'حدث خطأ ما' : err?.messageEn || 'Some error occurred',
            ),
        }),
    );
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }
}
